import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';

/** Supported production replay event types. */
export const PRODUCTION_EVENT_TYPES = {
  TASK_STARTED: 'task_started',
  TASK_PAUSED: 'task_paused',
  TASK_RESUMED: 'task_resumed',
  TASK_COMPLETED: 'task_completed',
  TASK_CANCELLED: 'task_cancelled',
  WORKER_ADDED: 'worker_added',
  WORKER_REMOVED: 'worker_removed',
  HANDOFF: 'handoff',
  ROOM_CHANGED: 'room_changed',
  MATERIAL_DELAY_LOGGED: 'material_delay_logged',
  NOTE_ADDED: 'note_added',
  QUANTITY_UPDATED: 'quantity_updated',
  TASK_EDITED: 'task_edited',
};

export const EVENT_TYPE_GROUPS = {
  active: new Set([
    PRODUCTION_EVENT_TYPES.TASK_STARTED,
    PRODUCTION_EVENT_TYPES.TASK_RESUMED,
    PRODUCTION_EVENT_TYPES.WORKER_ADDED,
  ]),
  pause: new Set([
    PRODUCTION_EVENT_TYPES.TASK_PAUSED,
  ]),
  delay: new Set([
    PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED,
  ]),
  handoff: new Set([
    PRODUCTION_EVENT_TYPES.HANDOFF,
    PRODUCTION_EVENT_TYPES.WORKER_ADDED,
    PRODUCTION_EVENT_TYPES.WORKER_REMOVED,
  ]),
  completion: new Set([
    PRODUCTION_EVENT_TYPES.TASK_COMPLETED,
    PRODUCTION_EVENT_TYPES.TASK_CANCELLED,
  ]),
  edit: new Set([
    PRODUCTION_EVENT_TYPES.TASK_EDITED,
    PRODUCTION_EVENT_TYPES.QUANTITY_UPDATED,
    PRODUCTION_EVENT_TYPES.ROOM_CHANGED,
  ]),
  note: new Set([
    PRODUCTION_EVENT_TYPES.NOTE_ADDED,
  ]),
};

export function taskDisplayName(task) {
  if (!task) return '';
  return task.isCustomTaskName && task.customTaskName
    ? task.customTaskName
    : (task.taskName || '');
}

/** Denormalized snapshot so replay stays readable after renames. */
export function buildTaskEventSnapshot(task) {
  if (!task) return {};
  return {
    taskName: taskDisplayName(task),
    taskType: task.taskType || null,
    taskCategoryId: task.taskCategoryId || null,
    taskCategoryName: task.taskCategoryName || null,
    productName: task.productName || null,
    roomId: task.roomId || null,
    roomName: task.roomName || null,
    employeeIds: task.employeeIds || [],
    employeeNames: task.employeeNames || [],
    teamSize: task.teamSize ?? null,
    assignmentType: task.assignmentType || null,
    tagIds: task.tagIds || [],
    tagNames: task.tagNames || [],
    status: task.status || null,
  };
}

/**
 * Append a production event. Failures are logged but never block the caller.
 */
export async function logProductionEvent({
  companyId,
  taskId,
  eventType,
  userId,
  task,
  snapshot,
  notes = null,
  reason = null,
  previousValue = null,
  newValue = null,
  durationMinutes = null,
  metadata = null,
  timestamp = null,
}) {
  if (!companyId || !taskId || !eventType || !userId) return;

  try {
    await addDoc(collection(db, COLLECTIONS.PRODUCTION_EVENTS), {
      companyId,
      taskId,
      eventType,
      timestamp: timestamp ?? serverTimestamp(),
      userId,
      createdBy: userId,
      ...buildTaskEventSnapshot(task),
      ...(snapshot || {}),
      notes,
      reason,
      previousValue,
      newValue,
      durationMinutes,
      metadata,
    });
  } catch (err) {
    console.error('[logProductionEvent]', eventType, err);
  }
}
